/**
 * The audio port's pure decisions (no WebAudio here, so they are unit-tested in Node): which voices a busy battle
 * keeps, whether a cue plays from the packs, the synth or not at all, and where a music bed's loop sits in its buffer.
 */

/** Cues a horde fires dozens of a second: the first to go when the mixer is busy. */
export function isLowPriority(cue: string): boolean { return cue === "hit" || cue.startsWith("fire_"); }

/** Concurrent sample voices at most; past it even a stinger is dropped rather than clip the limiter into mush. */
export const MaxVoices = 24;
/** Low-priority cues stop here, so the last voices stay free for deaths, leaks and stingers. */
export const LowPriorityVoices = 16;

/**
 * Cues that answer the player's own tap (a tower built, a road laid, a tier-up, the hub builder taking a job): one per
 * tap, never a flood, and the only confirmation the tap worked, so no crowd of shots and deaths may crowd them out.
 */
export function isPlayerAnswer(cue: string): boolean {
  return PLAYER_ANSWERS.has(cue);
}
/** The build cues, and the meta cues that answer a tap in a sheet (a gem pack, a crate, a trade, a queued job). */
const PLAYER_ANSWERS: ReadonlySet<string> = new Set([
  "towerPlace", "roadPlace", "towerUpgrade", "towerUpgradeMax", "hubUpgradeStart",
  "purchase", "crateOpen", "marketBuy", "marketSell", "queueSet",
]);

/** Whether a new voice for `cue` may start while `active` sample voices are still sounding. */
export function admitVoice(cue: string, active: number): boolean {
  if (isPlayerAnswer(cue)) return true;
  return active < (isLowPriority(cue) ? LowPriorityVoices : MaxVoices);
}

/**
 * Sample voices still sounding at `now`, from the context time each one ends (its start plus its buffer's length at
 * its playback rate), dropping the finished ones from `ends` in place. Counted by the clock rather than by `onended`:
 * a source whose `ended` event never arrives (WebKit, a context suspended or interrupted around it) would otherwise
 * hold its voice for good, and a cap full of ghosts silences every cue that is not a player answer.
 */
export function liveVoices(ends: number[], now: number): number {
  let n = 0;
  for (let i = 0; i < ends.length; i++) if (ends[i] > now) ends[n++] = ends[i];
  ends.length = n;
  return n;
}

/**
 * A player answer asked for while the context is not running yet (WebKit resumes it asynchronously from the same tap's
 * pointerdown, or after an interruption) still plays once it runs, if that is this soon (s); later it would land
 * detached from the tap, so it is dropped like every other cue.
 */
export const PlayerAnswerGraceSeconds = 0.35;

/** Whether a player answer held back at `askedAt` (s, performance clock) should still play at `now`. */
export function answerStillFresh(askedAt: number, now: number): boolean {
  return now - askedAt <= PlayerAnswerGraceSeconds;
}

/**
 * When and from where a cue asked for `delay` s from now starts. A negative delay means it is already that late (a
 * player answer held for WebKit's resume): it starts now, up to `lead` s into its sample — the part before its first
 * beat, so the beats still land on their frames — and past that it plays late but whole.
 */
export function cueStart(delay: number, lead: number): { wait: number; offset: number } {
  if (delay >= 0) return { wait: delay, offset: 0 };
  return { wait: 0, offset: Math.min(-delay, Math.max(0, lead)) };
}

/** The effect packs: manifest still loading, manifest read (variants decoding), or no manifest at all. */
export type PackState = "pending" | "ready" | "failed";
export type CueSource = "buffer" | "synth" | "none";

/**
 * Where a cue plays from. The synth stands in only when there is no pack (manifest failed), the pack has no such cue,
 * or its every variant failed to decode; while the pack is still arriving the cue stays silent, since a synth beep
 * that turns into the recorded sound a second later reads as a glitch.
 */
export function cueSource(pack: PackState, decoded: number | undefined, settled: boolean): CueSource {
  if (pack === "failed") return "synth";
  if (pack === "pending") return "none";
  if (decoded === undefined) return "synth";
  if (decoded > 0) return "buffer";
  return settled ? "synth" : "none";
}

/** A bed's loop from `music_manifest.json`: the composed length and the MP3 encoder + decoder delay (s). */
export interface BedLoop { loop: number; lead: number }

/**
 * A decoded buffer this much longer than the composed loop still carries the MP3 priming samples at its head (WebKit
 * may keep them; Chromium strips them using the LAME tag), so the loop is shifted by `lead`, or every seam would
 * repeat 23 ms of silence and drop the bar's first transient.
 */
export const LeadKeptSeconds = 0.01;

/** The loop window within the decoded buffer, or null to loop the whole buffer (no manifest, or it is not shorter). */
export function loopWindow(duration: number, bed: BedLoop | undefined): { start: number; end: number } | null {
  if (!bed || !(bed.loop < duration)) return null;
  if (bed.lead > 0 && duration - bed.loop > LeadKeptSeconds && bed.lead + bed.loop <= duration) return { start: bed.lead, end: bed.lead + bed.loop };
  return { start: 0, end: bed.loop };
}

/**
 * Where a looping bed is (buffer seconds) after `elapsed` s of play that began at `from`: straight on to the loop
 * window's end (the whole buffer without one), then round inside the window. A bed restarted on a new context
 * (audioLifecycle.ts: WebKit killed the old one) picks up here rather than at bar 1.
 */
export function bedPosition(from: number, elapsed: number, duration: number, span: { start: number; end: number } | null): number {
  const start = span?.start ?? 0, end = span?.end ?? duration;
  const t = from + Math.max(0, elapsed);
  if (t < end || !(end > start)) return Math.min(t, Math.max(start, end));
  return start + ((t - start) % (end - start));
}

/**
 * The last stand's music low-pass (ENGAGEMENT §B8): wide open outside it; on the line the bed loses its top above
 * `EdgeHz`, and at the last hit point it is a muffled `DeepHz` thud under the heartbeat. Exponential in between, as
 * pitch is heard. `DuckLevel` is the bed's level through the whole stand, so the heartbeat sits on top of it.
 */
export const LAST_STAND_MIX = { OpenHz: 20000, EdgeHz: 2200, DeepHz: 520, DuckLevel: 0.8, GlideSeconds: 0.45 } as const;

/** The music bus's low-pass cutoff (Hz) for a last-stand depth (lastStandDepth: 0..1), or wide open for null. */
export function lastStandCutoff(depth: number | null): number {
  if (depth === null) return LAST_STAND_MIX.OpenHz;
  const k = Math.max(0, Math.min(1, depth));
  return LAST_STAND_MIX.EdgeHz * Math.pow(LAST_STAND_MIX.DeepHz / LAST_STAND_MIX.EdgeHz, k);
}

/** The heartbeat's level (0..1): soft on the line, firmer as the fortress nears its end. */
export const HEARTBEAT_MIX = { Soft: 0.45, Hard: 0.85 } as const;
export function heartbeatGain(depth: number): number {
  const k = Math.max(0, Math.min(1, depth));
  return HEARTBEAT_MIX.Soft + (HEARTBEAT_MIX.Hard - HEARTBEAT_MIX.Soft) * k;
}

/**
 * The two buses under the master (Settings → Music and Effects, S2): each is its resting level times the player's 0..1
 * setting. Music rests well under the effects (0.34) so the build cues and stingers read over it on a phone speaker.
 */
export const BUS_LEVELS = { Music: 0.34, Sfx: 1.0 } as const;
export function busGains(music: number, sfx: number): { music: number; sfx: number } {
  const clamp = (v: number) => (Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 1);
  return { music: BUS_LEVELS.Music * clamp(music), sfx: BUS_LEVELS.Sfx * clamp(sfx) };
}

/**
 * The bed for a boss fight (A3): while `bossesAlive` > 0 the battle bed gives way to the boss bed; when the last boss
 * falls the battle bed comes back. Any other bed is left alone: the hub, and null once the level has ended (the win
 * or loss stinger has faded the music out, and a boss dying under it must not bring a bed back).
 */
export function bossBedFor(wanted: string | null, bossesAlive: number): string | null {
  if (wanted === "rare") return wanted;                     // M36: the rare's bed holds until the rare is gone (rareMoment.ts)
  if (bossesAlive > 0) return wanted === "battle" ? "boss" : wanted;
  return wanted === "boss" ? "battle" : wanted;
}

/**
 * Which of a cue's `count` variants plays (M38): a random one, never the one played last (`last`, −1 for none), so a
 * cue heard often — the wave's end, a horde's deaths — never repeats itself back to back. `r` is a 0..1 random draw.
 */
export function pickVariant(count: number, last: number, r: number): number {
  if (count <= 1) return 0;
  if (last < 0 || last >= count) return Math.min(count - 1, Math.floor(r * count));
  const i = Math.min(count - 2, Math.floor(r * (count - 1)));    // one of the others: skip over `last`
  return i >= last ? i + 1 : i;
}
